import React, { Fragment, useMemo } from 'react'
import PropTypes from 'prop-types'
import Edit from '@vtex/styleguide/lib/icon/Edit'
import { Link } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { Text, csx, dataAttr } from '@vtex/admin-ui'

import customerShape from '@/checkout-instore-legacy/customer/shapes'
import Box from '../../../components/Commons/Elements/Box'
import getClassStyle from '../../../utils/getClassStyle'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'

import CustomerProfileExtraData from './CustomerProfileExtraData'

const ALIGN_DATA = 'left'
const DATA_CONTENT_CLASSES = 'ml3'

const ClientData = ({ profile, orderForm }) => {
  const { extraFields } = getInstoreConfig().customerProfile

  const profileAddress = useMemo(() => {
    if (!profile || profile.length === 0) {
      return null
    }

    const fields = [
      'addressId',
      'addressType',
      'street',
      'number',
      'complement',
      'neighborhood',
      'city',
      'state',
      'country',
      'postalCode',
    ]
    const address = {}

    fields.forEach((field) => {
      if (profile[field]) {
        address[field] = profile[field].data
      }
    })

    return address
  }, [profile])

  if (!profile || profile.length === 0) {
    return null
  }

  const formatAddressToExtended = (address) => {
    const number = address.number ? `, ${address.number}` : ''
    const complement = address.complement ? `, ${address.complement}` : ''
    const postalCode = address.postalCode ? `, ${address.postalCode}` : ''
    const neighborhood = address.neighborhood ? `, ${address.neighborhood}` : ''
    const city = address.city ? `${address.city}/` : ''
    const streetAndNumber = address.street ? `${address.street}${number},` : ''

    return `${streetAndNumber} ${city}${address.state}${neighborhood}${complement}${postalCode}`
  }

  const orderFormCountry =
    orderForm &&
    orderForm.storePreferencesData &&
    orderForm.storePreferencesData.countryCode
  const customerCountry = profile.country && profile.country.data
  const profileCountry = customerCountry || orderFormCountry

  const customerEditRoute = getInstoreConfig().enableNewCheckout
    ? '/checkout/edit-profile/client-profile'
    : '/client-register'

  return (
    <Fragment>
      <Box i18nTitleId="customerInsightsClientDataTitle">
        <div className="lh-copy relative">
          <Link
            className="absolute right-0 top-0"
            to={customerEditRoute}
            id="edit-customer-profile-button"
          >
            <Edit color={getClassStyle('c-action-primary', 'color')} solid />
          </Link>
          <span>{profile.email && profile.email.data}</span>
          <table>
            <tbody>
              {profile.phone && (
                <tr>
                  <td>
                    <FormattedMessage id="customerProfileHomePhone" />
                    {': '}
                  </td>
                  <td align={ALIGN_DATA}>
                    <span className={DATA_CONTENT_CLASSES}>
                      {profile.phone.data}
                    </span>
                  </td>
                </tr>
              )}

              {profile.documentType && profile.document && (
                <tr>
                  <td>
                    <span className="ttu">
                      {profile.documentType.data}
                      {': '}
                    </span>
                  </td>
                  <td align={ALIGN_DATA}>
                    <span className={DATA_CONTENT_CLASSES}>
                      {profile.document.data}
                    </span>
                  </td>
                </tr>
              )}

              {extraFields && extraFields.length > 0 && (
                <CustomerProfileExtraData
                  profile={profile}
                  extraFields={extraFields}
                  alignData={ALIGN_DATA}
                  contentClasses={DATA_CONTENT_CLASSES}
                />
              )}
            </tbody>
          </table>
        </div>
      </Box>
      {profileCountry && (
        <div className="mt3 lh-copy">
          <Box>
            <Text
              variant="title2"
              className={csx({
                [dataAttr('variant', 'title2')]: {
                  color: '#727273',
                },
              })}
            >
              {formatAddressToExtended(profileAddress)}
            </Text>
            <br />
            <Text
              variant="title2"
              className={csx({
                [dataAttr('variant', 'title2')]: {
                  color: '#727273',
                },
              })}
            >
              {profileAddress.country ? `${profileAddress.country}` : ''}
            </Text>
          </Box>
        </div>
      )}
    </Fragment>
  )
}

ClientData.propTypes = {
  profile: customerShape,
  orderForm: PropTypes.object.isRequired,
}

export default ClientData
